import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, CalendarDays, Check, Clock, CalendarPlus } from 'lucide-react';
import { Sidebar } from '@/components/layout/sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScheduleFollowUp } from '@/components/follow-ups/schedule-follow-up';
import {
  useFollowUps,
  useUpdateFollowUp,
  useSnoozeFollowUp,
  type FollowUp,
} from '@/features/follow-ups/queries';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_auth/calendar')({ component: CalendarPage });

interface DayCell {
  date: Date;
  iso: string;
  inMonth: boolean;
  isToday: boolean;
}

function startOfMonth(d: Date): Date {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
}

function buildMonthGrid(anchor: Date): DayCell[] {
  const first = startOfMonth(anchor);
  const dayOfWeek = (first.getDay() + 6) % 7; // Mon=0
  const start = new Date(first);
  start.setDate(first.getDate() - dayOfWeek);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const cells: DayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push({
      date: d,
      iso: dateToIsoLocal(d),
      inMonth: d.getMonth() === anchor.getMonth(),
      isToday: d.getTime() === today.getTime(),
    });
  }
  return cells;
}

function dateToIsoLocal(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fuName(f: FollowUp): string {
  if (!f.contact) return 'No contact';
  const full = `${f.contact.first_name ?? ''} ${f.contact.last_name ?? ''}`.trim();
  return full || f.contact.company_name || 'No contact';
}

function CalendarPage(): JSX.Element {
  const [anchor, setAnchor] = useState<Date>(() => startOfMonth(new Date()));
  const [selectedIso, setSelectedIso] = useState<string>(() => dateToIsoLocal(new Date()));
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const { data, isLoading } = useFollowUps('all');
  const update = useUpdateFollowUp();
  const snooze = useSnoozeFollowUp();

  const followUps = data?.follow_ups ?? [];

  // Index by local YYYY-MM-DD
  const byDay = useMemo(() => {
    const m = new Map<string, FollowUp[]>();
    followUps.forEach((f) => {
      const due = new Date(f.due_at);
      const key = dateToIsoLocal(due);
      const list = m.get(key) ?? [];
      list.push(f);
      m.set(key, list);
    });
    // Sort each day by time ASC
    m.forEach((list) =>
      list.sort((a, b) => new Date(a.due_at).getTime() - new Date(b.due_at).getTime()),
    );
    return m;
  }, [followUps]);

  const cells = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const selectedFollowUps = byDay.get(selectedIso) ?? [];

  const monthLabel = anchor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const goPrev = (): void => {
    const d = new Date(anchor);
    d.setMonth(d.getMonth() - 1);
    setAnchor(startOfMonth(d));
  };
  const goNext = (): void => {
    const d = new Date(anchor);
    d.setMonth(d.getMonth() + 1);
    setAnchor(startOfMonth(d));
  };
  const goToday = (): void => {
    const t = new Date();
    setAnchor(startOfMonth(t));
    setSelectedIso(dateToIsoLocal(t));
  };

  return (
    <div className="flex h-screen bg-bg text-text">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="pl-14 pr-6 md:px-6 py-4 border-b border-border/[0.08] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-accent-2" />
            <h1 className="font-display text-lg">Calendar</h1>
            <span className="text-text-mute text-xs">
              {isLoading ? 'Loading…' : `${followUps.length} follow-up${followUps.length === 1 ? '' : 's'}`}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" onClick={goPrev} aria-label="Previous month">
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <div className="px-3 text-sm font-medium min-w-[140px] text-center">{monthLabel}</div>
            <Button size="sm" variant="outline" onClick={goNext} aria-label="Next month">
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline" onClick={goToday} className="ml-2">
              Today
            </Button>
            <Button size="sm" onClick={() => setScheduleOpen(true)} className="ml-2">
              <CalendarPlus className="h-3.5 w-3.5 mr-1" /> New follow-up
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 px-6 py-5">
          {/* Month grid */}
          <Card className="bg-surface border-border/[0.16]">
            <CardContent className="p-3">
              <div className="grid grid-cols-7 text-[10px] font-semibold uppercase tracking-wider text-text-mute mb-1.5">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                  <div key={d} className="px-1.5 py-1">
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {cells.map((c) => {
                  const items = byDay.get(c.iso) ?? [];
                  const selected = c.iso === selectedIso;
                  return (
                    <button
                      key={c.iso}
                      type="button"
                      onClick={() => setSelectedIso(c.iso)}
                      className={cn(
                        'min-h-[88px] rounded p-1.5 text-left transition-all border',
                        c.inMonth ? 'bg-surface-2' : 'bg-transparent',
                        c.inMonth ? 'text-text' : 'text-text-mute',
                        selected
                          ? 'border-accent ring-1 ring-accent/40'
                          : 'border-border/[0.08] hover:border-border/[0.24]',
                        c.isToday && !selected && 'border-accent-2/60',
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className={cn(
                            'text-[11px] font-mono',
                            c.isToday && 'font-semibold text-accent-2',
                          )}
                        >
                          {c.date.getDate()}
                        </span>
                        {items.length > 0 && (
                          <span className="text-[9px] font-mono rounded bg-accent/15 text-accent px-1">
                            {items.length}
                          </span>
                        )}
                      </div>
                      <div className="space-y-0.5">
                        {items.slice(0, 3).map((f) => {
                          const time = new Date(f.due_at).toLocaleTimeString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                          });
                          const colour =
                            f.status === 'done' || f.status === 'dismissed'
                              ? 'bg-success/15 text-success line-through'
                              : new Date(f.due_at).getTime() < Date.now()
                                ? 'bg-warn/15 text-warn'
                                : 'bg-accent/10 text-accent';
                          return (
                            <div
                              key={f.id}
                              className={cn(
                                'truncate rounded px-1 py-0.5 text-[10px] leading-tight',
                                colour,
                              )}
                              title={`${time} · ${fuName(f)}${f.note ? ' · ' + f.note : ''}`}
                            >
                              <span className="font-mono">{time}</span> {fuName(f)}
                            </div>
                          );
                        })}
                        {items.length > 3 && (
                          <div className="text-[9px] text-text-mute pl-1">
                            +{items.length - 3} more
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Day detail rail */}
          <Card className="bg-surface border-border/[0.16]">
            <CardHeader>
              <CardTitle className="text-sm">
                {new Date(selectedIso + 'T00:00:00').toLocaleDateString('en-GB', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedFollowUps.length === 0 ? (
                <p className="text-text-mute text-sm">No follow-ups scheduled for this day.</p>
              ) : (
                <ul className="space-y-2">
                  {selectedFollowUps.map((f) => {
                    const time = new Date(f.due_at).toLocaleTimeString('en-GB', {
                      hour: '2-digit',
                      minute: '2-digit',
                    });
                    const overdue = new Date(f.due_at).getTime() < Date.now();
                    const isOpen = f.status === 'pending' || f.status === 'snoozed';
                    return (
                      <li
                        key={f.id}
                        className={cn(
                          'rounded border p-2.5',
                          isOpen
                            ? overdue
                              ? 'border-warn/40 bg-warn/5'
                              : 'border-border/[0.16] bg-surface-2'
                            : 'border-border/[0.08] bg-surface-2 opacity-70',
                        )}
                      >
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                          <div className="font-mono text-[11px] text-text-dim">{time}</div>
                          <span
                            className={cn(
                              'text-[10px] uppercase tracking-wider font-semibold',
                              f.status === 'pending' && (overdue ? 'text-warn' : 'text-accent'),
                              f.status === 'snoozed' && 'text-accent-2',
                              f.status === 'done' && 'text-success',
                              f.status === 'dismissed' && 'text-text-mute',
                            )}
                          >
                            {overdue && f.status === 'pending' ? 'overdue' : f.status}
                          </span>
                        </div>
                        <div className="text-sm font-medium text-text">
                          {f.contact?.id ? (
                            <Link
                              to="/contacts/$id"
                              params={{ id: f.contact.id }}
                              className="hover:underline"
                            >
                              {fuName(f)}
                            </Link>
                          ) : (
                            fuName(f)
                          )}
                        </div>
                        {f.note && (
                          <div className="text-xs text-text-dim mt-0.5 whitespace-pre-wrap">
                            {f.note}
                          </div>
                        )}
                        {isOpen && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <button
                              type="button"
                              onClick={() =>
                                void update.mutateAsync({ id: f.id, patch: { status: 'done' } })
                              }
                              className="rounded border border-success/40 bg-success/10 px-2 py-0.5 text-[11px] font-semibold text-success hover:bg-success/20 transition flex items-center gap-1"
                            >
                              <Check className="h-3 w-3" /> Done
                            </button>
                            <button
                              type="button"
                              onClick={() => void snooze.mutateAsync({ id: f.id, minutes: 60 })}
                              className="rounded border border-border/[0.16] bg-surface-2 px-2 py-0.5 text-[11px] font-medium text-text-dim hover:text-text transition flex items-center gap-1"
                            >
                              <Clock className="h-3 w-3" /> Snooze 1h
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <ScheduleFollowUp
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
          contactId={null}
          contactName={null}
        />
      </main>
    </div>
  );
}
